import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import { Button, Input, Select, message } from "antd";
import React, { useEffect, useState } from "react";
import { Form, redirect, useActionData, useNavigate } from "react-router-dom";
import { createUser } from "../../api/user";
import { getCloudTalkAgents } from "../../api/telephony/cloudtalk";
import { ResponsiveSideDrawer } from "../../components/drawer";
import { FieldWrapper } from "../../components/inputs/input-wrappers.jsx";
import { ValidationError } from "../../library/classes/exceptions/index.js";
import {
  Permission,
  RolePermission,
  UserRoles,
} from "../../library/constants/dynamic.js";
import { generateRouteUrl } from "../../library/constants/routes.js";
import {
  getFieldErrors,
  getFieldStatus,
} from "../../library/helpers/forms/index.js";
import Styles from "./user-details.module.scss";
import { normalizeEmail } from "../../library/helpers/normalize.js";

const UserCreate = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [meetingLink, setMeetingLink] = useState("");
  const [cloudtalkAgentId, setCloudtalkAgentId] = useState(null);
  const [cloudtalkAgents, setCloudtalkAgents] = useState([]);
  const [userType, setUserType] = useState([]);
  const [permission, setPermission] = useState([]);
  const { errors = null } = useActionData() || {};

  useEffect(() => {
    getCloudTalkAgents()
      .then((agents) => {
        if (Array.isArray(agents)) {
          setCloudtalkAgents(agents);
        }
      })
      .catch(() => setCloudtalkAgents([]));
  }, []);

  const handleRolesChange = (roles) => {
    // If the roles array is empty, reset permissions
    if (roles.length === 0) {
      setUserType([]);
      setPermission([]);
      return;
    }

    let newPermissions = [];
    setUserType(roles);
    roles.forEach((r) => {
      const rolePermissions =
        RolePermission.find(({ id }) => r === id)?.value || [];
      newPermissions = [...newPermissions, ...rolePermissions];
    });
    setPermission([...new Set(newPermissions)]);
  };

  const agentOptions = cloudtalkAgents.map((a) => {
    const assignedLabel = a.assigned_to
      ? ` [Assigned to: ${a.assigned_to.name || a.assigned_to.email}]`
      : "";
    return {
      value: String(a.id),
      label: `${a.name || "Agent " + a.id} (${a.id}${a.email ? ` • ${a.email}` : ""})${assignedLabel}`,
    };
  });

  return (
    <ResponsiveSideDrawer
      open={true}
      desktopWidth={ResponsiveSideDrawer.DRAWER_WIDTH_WIDE}
      title="Create User"
      onClose={() => navigate("..", { replace: true, relative: "route" })}
    >
      <div className={Styles.drawerContent}>
        <div className={Styles.drawerScrollArea}>
          <Form method="post" className={Styles.formWrapper}>
        <div className={Styles.fieldsRow}>
          <div className={Styles.inputField}>
            <FieldWrapper label="First Name" showRequired>
              <Input
                type="text"
                name="first_name"
                required
                status={getFieldStatus("first_name", errors)}
              />
            </FieldWrapper>
            {getFieldErrors("first_name", errors)}
          </div>
          <div className={Styles.inputField}>
            <FieldWrapper label="Last Name">
              <Input
                type="text"
                name="last_name"
                status={getFieldStatus("last_name", errors)}
              />
            </FieldWrapper>
            {getFieldErrors("last_name", errors)}
          </div>
        </div>

        <div className={Styles.fieldsWrapper}>
          <div className={Styles.inputField}>
            <FieldWrapper label="Email" showRequired>
              <Input
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(normalizeEmail(e.target.value))}
                status={getFieldStatus("email", errors)}
              />
            </FieldWrapper>
            {getFieldErrors("email", errors)}
          </div>
        </div>

        <div className={Styles.fieldsWrapper}>
          <div className={Styles.inputField}>
            <FieldWrapper label="Meeting Link">
              <Input
                type="text"
                name="meeting_link"
                value={meetingLink}
                onChange={(e) => setMeetingLink(e.target.value)}
              />
            </FieldWrapper>
          </div>
        </div>

        <div className={Styles.fieldsWrapper}>
          <div className={Styles.inputField}>
            <FieldWrapper label="CloudTalk Agent">
              <Select
                placeholder="Select CloudTalk Agent (optional)"
                allowClear
                showSearch
                optionFilterProp="label"
                value={cloudtalkAgentId}
                onChange={(val) => setCloudtalkAgentId(val)}
                options={agentOptions}
              />
            </FieldWrapper>
            <input
              type="hidden"
              name="cloudtalk_agent_id"
              value={cloudtalkAgentId || ""}
            />
          </div>
        </div>

        <div className={Styles.fieldsWrapper}>
          <div className={Styles.inputField}>
            <FieldWrapper label="Roles" showRequired>
              <Select
                options={UserRoles}
                fieldNames={{ value: "id", label: "name" }}
                onChange={handleRolesChange}
                mode="multiple"
                status={getFieldStatus("type", errors)}
                required
              />
            </FieldWrapper>
            {getFieldErrors("type", errors)}
            <input type="hidden" name="type" value={userType} />
          </div>
        </div>

        <div className={Styles.fieldsWrapper}>
          <div className={Styles.inputField}>
            <FieldWrapper label="Permission" showRequired>
              <Select
                options={Permission}
                mode="multiple"
                value={permission}
                fieldNames={{ value: "id", label: "name" }}
                defaultValue={permission}
                onChange={(c) => setPermission(c)}
                status={getFieldStatus("type", errors)}
                required
              />
            </FieldWrapper>
            {getFieldErrors("type", errors)}
            <input type="hidden" name="permission" value={permission} />
          </div>
        </div>

        <div className={Styles.fieldsWrapper}>
          <div className={Styles.inputField}>
            <FieldWrapper label="New Password" showRequired>
              <Input.Password
                placeholder="New Password"
                name="password"
                iconRender={(visible) =>
                  visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
                status={getFieldStatus("name", errors)}
                pattern="^(?=.*[0-9])(?=.*[\W_])(?=.*[a-zA-Z])(?=\S+$).{6,}$"
                title="Password must contain at least one digit, one special character, one letter, and be at least 6 characters."
                required
              />
            </FieldWrapper>
            {getFieldErrors("name", errors)}
          </div>
        </div>

        <Button type="primary" htmlType="submit">
          Create User
        </Button>
          </Form>
        </div>
      </div>
    </ResponsiveSideDrawer>
  );
};

UserCreate.Action = async ({ request }) => {
  const {
    first_name,
    last_name,
    email,
    type,
    permission,
    meeting_link,
    cloudtalk_agent_id,
    password,
  } = Object.fromEntries(await request.formData());

  try {
    //Todo: Basic validation
    const { message: msg, data } = await createUser({
      first_name,
      last_name,
      email,
      // username,
      meeting_link,
      cloudtalk_agent_id: cloudtalk_agent_id ? Number(cloudtalk_agent_id) : null,
      user_roles: [...type.split(","), ...permission.split(",")],
      password,
    });
    message.success(msg);
    return redirect(
      generateRouteUrl("UserDetails", {
        userId: data.id,
      }),
      {
        replace: true,
      },
    );
  } catch (error) {
    message.error(error.message);
    if (error instanceof ValidationError) {
      return {
        errors: error.errors,
      };
    }
  }
  return false;
};

export default UserCreate;
